import { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Loading } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import type { Market } from '../dataProvider/types';
import { FraudUserCard } from './FraudUserCard';
import { useFraudQueue } from './useFraudQueue';

/**
 * Fraud queue - the LEAD surface of Phase 5. A ranked list of flagged users
 * (highest riskScore first), each with an evidence panel and the guarded
 * recorded-decision actions (suspend / ban / escalate / reinstate). This surface
 * carries the phase foundation: the fraud-reviewer role, riskScore + fraudStatus on
 * Users, and the review-action pattern the other fraud surfaces inherit
 * (D-fraud-queue).
 *
 * INVESTIGATIVE-GLOBAL: it does NOT read the region switcher (a fraud ring spans
 * markets). It is global by default; the region facet below filters the user's
 * market by exact value, not through marketsForScope.
 *
 * SENSITIVE: gated by ROLE, not capability - a capability read gate would let the
 * read-everywhere viewer in via its '*:read' wildcard, the same reasoning that keeps
 * the audit log role-gated. Only the fraud reviewer and super_admin reach it.
 */

/** The surface without the access wrapper. Exported so tests can inject `asOf`
 *  (the clock a suspension period is measured from). */
export const FraudQueueView = ({ asOf }: { asOf?: number }) => {
  const [now] = useState<number>(() => asOf ?? Date.now());
  const [regionFilter, setRegionFilter] = useState<Market | null>(null);
  const { view, isLoading } = useFraudQueue(regionFilter);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Trust and safety
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Fraud queue
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Users ranked by risk, highest first. Every action is a recorded decision
        the system enforces, never a direct change to a balance. Reasons are
        captured into the audit trail. The view spans both markets by default,
        so a ring across markets stays visible.
      </Typography>

      <FormControl size="small" sx={{ minWidth: 200, mb: 3 }}>
        <InputLabel id="fraud-region-label">Region</InputLabel>
        <Select
          labelId="fraud-region-label"
          label="Region"
          value={regionFilter ?? 'all'}
          inputProps={{ 'data-testid': 'fraud-region' }}
          onChange={(e) =>
            setRegionFilter(
              e.target.value === 'all' ? null : (e.target.value as Market),
            )
          }
        >
          <MenuItem value="all">All markets</MenuItem>
          <MenuItem value="uk">UK</MenuItem>
          <MenuItem value="us">US</MenuItem>
        </Select>
      </FormControl>

      {isLoading || !view ? (
        <Loading />
      ) : view.rows.length === 0 ? (
        <Typography color="text.secondary">
          No flagged users in scope.
        </Typography>
      ) : (
        view.rows.map((row) => (
          <FraudUserCard key={row.id} row={row} now={now} />
        ))
      )}
    </Box>
  );
};

/**
 * Gated entry registered at /fraud-queue. ROLE-gated to fraud and super_admin: the
 * trail is sensitive (like the audit log), so a viewer's read-everywhere wildcard
 * must not reach it, and an editor has no business here either (D-fraud-queue).
 */
export const FraudQueue = () => (
  <RequireAccess roles={['fraud', 'super_admin']}>
    <FraudQueueView />
  </RequireAccess>
);
